import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Card, Typography, Row, Col, Statistic, DatePicker, Space, Select, Spin,
  Alert, Tag, Button, Modal, Form, Input, InputNumber, Switch, Progress, Popconfirm,
  message, Tooltip,
} from 'antd';
import Table from 'components/Common/Table';
import {
  PlusOutlined, EditOutlined, DeleteOutlined, WalletOutlined, RiseOutlined,
  WarningOutlined, CheckCircleOutlined, ReloadOutlined,
} from '@ant-design/icons';
import {
  Bar, BarChart, CartesianGrid, Cell, ComposedChart, Legend, Line, ResponsiveContainer,
  Tooltip as RechartsTooltip, XAxis, YAxis,
} from 'recharts';
import axios from 'axios';
import dayjs from 'dayjs';
import useIsMobile from '../../hooks/useIsMobile';
import ExportPdfButton from '../../components/Reports/ExportPdfButton';
import useUrlFilters from 'hooks/useUrlFilters';
import { ANT_COLOR_HEX } from '../../utils/categoryColors';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;
const { Option } = Select;
const { TextArea } = Input;

const STATUS_META = {
  ON_TRACK: { label: 'On Track', color: 'green', progress: 'normal' },
  AT_RISK: { label: 'At Risk', color: 'orange', progress: 'active' },
  OVER_BUDGET: { label: 'Over Budget', color: 'red', progress: 'exception' },
};

const PERIOD_PRESETS = {
  MONTHLY: () => [dayjs().startOf('month'), dayjs().endOf('month')],
  QUARTERLY: () => [dayjs().startOf('quarter'), dayjs().endOf('quarter')],
  YEARLY: () => [dayjs().startOf('year'), dayjs().endOf('year')],
};

const URL_FILTERS = {
  category: { type: 'string', default: 'all' },
  active: { type: 'string', key: 'is_active', default: 'true' },
  period: { type: 'dateRange', keys: ['start_date', 'end_date'] },
};

const currency = (value) => `₹${Number(value || 0).toLocaleString('en-IN')}`;

const Budgets = () => {
  const isMobile = useIsMobile();
  const reportRef = useRef(null);
  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [categories, setCategories] = useState([]);

  const [filters, setFilters] = useUrlFilters(URL_FILTERS);
  const urlSetter = (name) => (value) => setFilters({ [name]: value });
  const categoryFilter = filters.category;
  const setCategoryFilter = urlSetter('category');
  const statusFilter = filters.active;   // is_active
  const setStatusFilter = urlSetter('active');
  const dateRange = filters.period;
  const setDateRange = urlSetter('period');

  const [modalOpen, setModalOpen] = useState(false);
  const [editingBudget, setEditingBudget] = useState(null);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = {
        is_active: statusFilter,
        filter_preset: dateRange ? undefined : 'all_time',
        start_date: dateRange?.[0] ? dateRange[0].format('YYYY-MM-DD') : undefined,
        end_date: dateRange?.[1] ? dateRange[1].format('YYYY-MM-DD') : undefined,
      };
      if (categoryFilter !== 'all') params.category = categoryFilter;

      const response = await axios.get('/accounting/budget/summary/', { params });
      if (response.status === 200) setData(response.data);
    } catch (err) {
      console.error('Error fetching budgets:', err);
      setError(err.response?.data?.error || 'Failed to load budgets');
    } finally {
      setLoading(false);
    }
  }, [categoryFilter, statusFilter, dateRange]);

  useEffect(() => { fetchSummary(); }, [fetchSummary]);

  useEffect(() => {
    axios.get('/accounting/expense/categories/')
      .then((response) => setCategories(response.data || []))
      .catch((err) => console.error('Error fetching categories:', err));
  }, []);

  const openCreate = () => {
    setEditingBudget(null);
    form.resetFields();
    form.setFieldsValue({
      period_type: 'MONTHLY',
      period: PERIOD_PRESETS.MONTHLY(),
      alert_threshold: 80,
      include_payroll: false,
      is_active: true,
    });
    setModalOpen(true);
  };

  const openEdit = (budget) => {
    setEditingBudget(budget);
    form.setFieldsValue({
      name: budget.name,
      category: budget.category || undefined,
      period_type: budget.period_type,
      period: [dayjs(budget.start_date), dayjs(budget.end_date)],
      amount: budget.amount,
      alert_threshold: budget.alert_threshold,
      include_payroll: budget.include_payroll,
      is_active: budget.is_active,
      notes: budget.notes,
    });
    setModalOpen(true);
  };

  // Picking a period type fills the dates in; CUSTOM leaves them alone.
  const handlePeriodTypeChange = (periodType) => {
    const preset = PERIOD_PRESETS[periodType];
    if (preset) form.setFieldsValue({ period: preset() });
  };

  const handleSubmit = async (values) => {
    setSaving(true);
    try {
      const payload = {
        name: values.name,
        category: values.category || null,
        period_type: values.period_type,
        start_date: values.period[0].format('YYYY-MM-DD'),
        end_date: values.period[1].format('YYYY-MM-DD'),
        amount: values.amount,
        alert_threshold: values.alert_threshold,
        include_payroll: values.include_payroll,
        is_active: values.is_active,
        notes: values.notes || null,
      };

      if (editingBudget) {
        await axios.patch(`/accounting/budget/${editingBudget.id}/`, payload);
        message.success('Budget updated');
      } else {
        await axios.post('/accounting/budget/', payload);
        message.success('Budget created');
      }
      setModalOpen(false);
      setEditingBudget(null);
      form.resetFields();
      fetchSummary();
    } catch (err) {
      console.error('Error saving budget:', err);
      const detail = err.response?.data;
      const firstError = detail && typeof detail === 'object'
        ? Object.values(detail).flat()[0]
        : null;
      message.error(firstError || 'Could not save this budget');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (budget) => {
    try {
      await axios.delete(`/accounting/budget/${budget.id}/`);
      message.success('Budget deleted');
      fetchSummary();
    } catch (err) {
      console.error('Error deleting budget:', err);
      message.error('Could not delete this budget');
    }
  };

  const summary = data?.summary;
  const budgets = data?.budgets || [];

  const categoryChartData = (data?.by_category || []).map((item, index) => ({
    name: item.category_name,
    budgeted: item.budgeted,
    spent: item.spent,
    fill: ANT_COLOR_HEX[item.category_color] || ['#1890ff', '#52c41a', '#faad14', '#eb2f96'][index % 4],
  }));

  const utilizationChartData = budgets.map((budget) => ({
    name: budget.name.length > 18 ? `${budget.name.substring(0, 18)}...` : budget.name,
    utilization: budget.utilization,
    fill: budget.status === 'OVER_BUDGET' ? '#ff4d4f' : budget.status === 'AT_RISK' ? '#faad14' : '#52c41a',
  }));

  const columns = [
    {
      title: 'Budget',
      dataIndex: 'name',
      key: 'name',
      render: (name, record) => (
        <Space direction="vertical" size={0}>
          <Text strong>{name}</Text>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {record.start_date} to {record.end_date}
          </Text>
        </Space>
      ),
      width: 200,
    },
    {
      title: 'Category',
      dataIndex: 'category_name',
      key: 'category_name',
      render: (name, record) => <Tag color={record.category_color || 'default'}>{name}</Tag>,
      width: 140,
    },
    {
      title: 'Period',
      dataIndex: 'period_type',
      key: 'period_type',
      width: 100,
      responsive: ['lg'],
    },
    {
      title: 'Budgeted',
      dataIndex: 'amount',
      key: 'amount',
      render: (value) => currency(value),
      align: 'right',
      sorter: (a, b) => a.amount - b.amount,
      width: 120,
    },
    {
      title: 'Spent',
      dataIndex: 'spent',
      key: 'spent',
      render: (value) => <Text type="danger">{currency(value)}</Text>,
      align: 'right',
      sorter: (a, b) => a.spent - b.spent,
      width: 120,
    },
    {
      title: 'Remaining',
      dataIndex: 'remaining',
      key: 'remaining',
      render: (value) => (
        <Text type={value < 0 ? 'danger' : 'success'}>{currency(value)}</Text>
      ),
      align: 'right',
      width: 120,
    },
    {
      title: 'Utilization',
      dataIndex: 'utilization',
      key: 'utilization',
      render: (value, record) => (
        <Tooltip title={`Expected by now: ${record.expected_utilization}%`}>
          <Progress
            percent={Math.min(value, 100)}
            format={() => `${value}%`}
            size="small"
            status={STATUS_META[record.status]?.progress}
          />
        </Tooltip>
      ),
      sorter: (a, b) => a.utilization - b.utilization,
      width: 160,
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status) => (
        <Tag color={STATUS_META[status]?.color}>{STATUS_META[status]?.label || status}</Tag>
      ),
      width: 120,
    },
    {
      title: 'Days Left',
      dataIndex: 'days_remaining',
      key: 'days_remaining',
      align: 'right',
      width: 90,
      responsive: ['md'],
    },
    {
      title: 'Actions',
      key: 'actions',
      render: (_, record) => (
        <Space size="small">
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
          <Popconfirm
            title="Delete this budget?"
            description="The expenses it tracks are not affected."
            onConfirm={() => handleDelete(record)}
            okText="Delete"
            okButtonProps={{ danger: true }}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
      width: 100,
      fixed: isMobile ? false : 'right',
    },
  ];

  // Tables for the PDF export, passed explicitly so every row is included.
  const pdfTables = [
    {
      title: 'Budget vs Actual',
      columns: [
        { header: 'Budget', key: 'name' },
        { header: 'Category', key: 'category_name' },
        { header: 'Period', key: 'period_type' },
        { header: 'Start', key: 'start_date' },
        { header: 'End', key: 'end_date' },
        { header: 'Budgeted', key: 'amount', align: 'right', format: 'currency' },
        { header: 'Spent', key: 'spent', align: 'right', format: 'currency' },
        { header: 'Remaining', key: 'remaining', align: 'right', format: 'currency' },
        { header: 'Utilization', key: 'utilization', align: 'right', format: 'percent' },
        { header: 'Status', key: 'status', format: (value) => STATUS_META[value]?.label || value },
      ],
      rows: budgets,
    },
    {
      title: 'By Category',
      columns: [
        { header: 'Category', key: 'category_name' },
        { header: 'Budgets', key: 'budget_count', align: 'right' },
        { header: 'Budgeted', key: 'budgeted', align: 'right', format: 'currency' },
        { header: 'Spent', key: 'spent', align: 'right', format: 'currency' },
        { header: 'Remaining', key: 'remaining', align: 'right', format: 'currency' },
        { header: 'Utilization', key: 'utilization', align: 'right', format: 'percent' },
      ],
      rows: data?.by_category || [],
    },
    {
      title: 'Monthly Budget vs Actual',
      columns: [
        { header: 'Month', key: 'month' },
        { header: 'Budgeted', key: 'budgeted', align: 'right', format: 'currency' },
        { header: 'Actual', key: 'actual', align: 'right', format: 'currency' },
        { header: 'Variance', key: 'variance', align: 'right', format: 'currency' },
      ],
      rows: data?.monthly_trend || [],
    },
  ];

  const pdfMeta = [
    { label: 'Budgeted', value: `Rs. ${(summary?.total_budgeted || 0).toLocaleString('en-IN')}` },
    { label: 'Spent', value: `Rs. ${(summary?.total_spent || 0).toLocaleString('en-IN')}` },
    { label: 'Utilization', value: `${summary?.utilization || 0}%` },
    { label: 'Budgets', value: summary?.budget_count || 0 },
  ];

  return (
    <div ref={reportRef} style={{
      height: 'calc(100vh - 64px)',
      overflow: 'auto',
      padding: isMobile ? '12px' : '16px 24px',
      backgroundColor: '#f0f2f5',
    }}>
      {/* Header */}
      <div style={{
        marginBottom: '16px',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        flexWrap: 'wrap',
        gap: '12px',
      }}>
        <Title level={isMobile ? 4 : 2} style={{ margin: 0 }}>Budgets</Title>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
          <ExportPdfButton
            reportRef={reportRef}
            title="Budget Report"
            subtitle={summary?.date_range}
            meta={pdfMeta}
            tables={pdfTables}
            fileName="budget-report"
            orientation="landscape"
            disabled={loading || !data}
            type="default"
          />
          <Tooltip title="Refresh">
            <Button size="small" icon={<ReloadOutlined />} onClick={fetchSummary} aria-label="Refresh" />
          </Tooltip>
          <Button type="primary" size="small" icon={<PlusOutlined />} onClick={openCreate}>
            New Budget
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card size="small" style={{ marginBottom: '16px' }}>
        <Space wrap size="middle">
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>Period overlapping:</Text>
            <RangePicker
              value={dateRange}
              onChange={(dates) => setDateRange(dates && dates[0] && dates[1] ? dates : null)}
              format="YYYY-MM-DD"
              size="small"
              allowClear
            />
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>Category:</Text>
            <Select value={categoryFilter} onChange={setCategoryFilter} size="small" style={{ width: 180 }}>
              <Option value="all">All Categories</Option>
              {categories.map((category) => (
                <Option key={category.value || category.id} value={String(category.value || category.id)}>
                  {category.label || category.name}
                </Option>
              ))}
            </Select>
          </div>
          <div>
            <Text strong style={{ display: 'block', marginBottom: 8, fontSize: '13px' }}>Status:</Text>
            <Select value={statusFilter} onChange={setStatusFilter} size="small" style={{ width: 140 }}>
              <Option value="true">Active</Option>
              <Option value="false">Inactive</Option>
              <Option value="all">All</Option>
            </Select>
          </div>
        </Space>
      </Card>

      {error && (
        <Alert
          message="Error" description={error} type="error" showIcon closable
          onClose={() => setError(null)} style={{ marginBottom: '16px' }}
        />
      )}

      {loading && (
        <Card size="small">
          <div style={{ textAlign: 'center', padding: '40px 20px' }}>
            <Spin size="large" />
            <div style={{ marginTop: '16px' }}><Text>Loading budgets...</Text></div>
          </div>
        </Card>
      )}

      {!loading && data && (
        <>
          {/* Summary */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Total Budgeted</span>}
                  value={summary?.total_budgeted || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#1890ff' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Spent</span>}
                  value={summary?.total_spent || 0}
                  prefix={<><WalletOutlined style={{ marginRight: 4 }} />₹</>}
                  precision={0}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px', color: '#ff4d4f' }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Remaining</span>}
                  value={summary?.total_remaining || 0}
                  prefix="₹"
                  precision={0}
                  valueStyle={{
                    fontSize: isMobile ? '16px' : '22px',
                    color: (summary?.total_remaining || 0) < 0 ? '#ff4d4f' : '#52c41a',
                  }}
                />
              </Card>
            </Col>
            <Col xs={12} sm={12} md={6}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '12px' : '20px' }}>
                <Statistic
                  title={<span style={{ fontSize: isMobile ? '12px' : '14px' }}>Utilization</span>}
                  value={summary?.utilization || 0}
                  suffix="%"
                  precision={1}
                  prefix={<RiseOutlined />}
                  valueStyle={{ fontSize: isMobile ? '16px' : '22px' }}
                />
              </Card>
            </Col>
          </Row>

          {/* Status counts */}
          <Row gutter={[8, 8]} style={{ marginBottom: '16px' }}>
            <Col xs={8}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '10px' : '16px' }}>
                <Statistic
                  title={<span style={{ fontSize: '12px' }}>On Track</span>}
                  value={summary?.on_track_count || 0}
                  prefix={<CheckCircleOutlined />}
                  valueStyle={{ fontSize: isMobile ? '14px' : '18px', color: '#52c41a' }}
                />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '10px' : '16px' }}>
                <Statistic
                  title={<span style={{ fontSize: '12px' }}>At Risk</span>}
                  value={summary?.at_risk_count || 0}
                  prefix={<WarningOutlined />}
                  valueStyle={{ fontSize: isMobile ? '14px' : '18px', color: '#faad14' }}
                />
              </Card>
            </Col>
            <Col xs={8}>
              <Card size="small" bodyStyle={{ padding: isMobile ? '10px' : '16px' }}>
                <Statistic
                  title={<span style={{ fontSize: '12px' }}>Over Budget</span>}
                  value={summary?.over_budget_count || 0}
                  prefix={<WarningOutlined />}
                  valueStyle={{ fontSize: isMobile ? '14px' : '18px', color: '#ff4d4f' }}
                />
              </Card>
            </Col>
          </Row>

          {(data.alerts || []).length > 0 && (
            <Alert
              type="warning"
              showIcon
              style={{ marginBottom: '16px' }}
              message={`${data.alerts.length} budget(s) need attention`}
              description={
                <Space direction="vertical" size={2} style={{ width: '100%' }}>
                  {data.alerts.slice(0, 5).map((alert) => (
                    <Text key={alert.id} style={{ fontSize: 12 }}>
                      <Tag color={STATUS_META[alert.status]?.color}>{STATUS_META[alert.status]?.label}</Tag>
                      {alert.name} ({alert.category_name}) - {alert.utilization}% used,
                      {' '}{currency(alert.spent)} of {currency(alert.amount)}
                    </Text>
                  ))}
                </Space>
              }
            />
          )}

          {/* Charts */}
          <Row gutter={[16, 16]} style={{ marginBottom: '16px' }}>
            <Col xs={24} md={12}>
              <Card size="small" title={<Text strong>Budget vs Actual by Category</Text>}>
                {categoryChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                    <BarChart data={categoryChartData} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="name" tick={{ fontSize: isMobile ? 9 : 11 }} angle={-30} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 40 : 60} />
                      <RechartsTooltip formatter={(value) => currency(value)} />
                      <Legend />
                      <Bar dataKey="budgeted" fill="#1890ff" name="Budgeted" />
                      <Bar dataKey="spent" fill="#ff4d4f" name="Spent" />
                    </BarChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No budget data available</Text></div>
                )}
              </Card>
            </Col>

            <Col xs={24} md={12}>
              <Card size="small" title={<Text strong>Monthly Budget vs Actual</Text>}>
                {(data.monthly_trend || []).length > 0 ? (
                  <ResponsiveContainer width="100%" height={isMobile ? 220 : 280}>
                    <ComposedChart data={data.monthly_trend} margin={{ top: 20, right: isMobile ? 10 : 30, left: isMobile ? 0 : 20, bottom: 5 }}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis dataKey="month" tick={{ fontSize: isMobile ? 9 : 11 }} angle={-45} textAnchor="end" height={60} />
                      <YAxis tick={{ fontSize: isMobile ? 9 : 11 }} width={isMobile ? 40 : 60} />
                      <RechartsTooltip formatter={(value) => currency(value)} />
                      <Legend />
                      <Bar dataKey="budgeted" fill="#91caff" name="Budgeted" />
                      <Line type="monotone" dataKey="actual" stroke="#ff4d4f" strokeWidth={2} name="Actual" dot={{ r: 3 }} />
                    </ComposedChart>
                  </ResponsiveContainer>
                ) : (
                  <div style={{ textAlign: 'center', padding: '40px' }}><Text type="secondary">No monthly data available</Text></div>
                )}
              </Card>
            </Col>
          </Row>

          {utilizationChartData.length > 0 && (
            <Card size="small" title={<Text strong>Utilization by Budget</Text>} style={{ marginBottom: '16px' }}>
              <ResponsiveContainer width="100%" height={Math.max(180, utilizationChartData.length * 32)}>
                <BarChart data={utilizationChartData} layout="vertical" margin={{ top: 10, right: 30, left: isMobile ? 10 : 60, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis type="number" unit="%" tick={{ fontSize: 11 }} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11 }} width={isMobile ? 90 : 150} />
                  <RechartsTooltip formatter={(value) => `${value}%`} />
                  <Bar dataKey="utilization" name="Utilization">
                    {utilizationChartData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.fill} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          )}

          {/* Budget table */}
          <Card size="small" title={
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
              <Text strong>Budget vs Actual</Text>
              {!isMobile && <Text type="secondary" style={{ fontSize: '12px' }}>{summary?.date_range}</Text>}
            </div>
          }>
            <Table
              columns={columns}
              dataSource={budgets}
              rowKey="id"
              size="small"
              pagination={{
                defaultPageSize: isMobile ? 5 : 10,
                pageSizeOptions: ['5', '10', '20', '50'],
                showSizeChanger: !isMobile,
                simple: isMobile,
              }}
              scroll={{ x: 'max-content' }}
              locale={{ emptyText: 'No budgets yet. Create one to start tracking spend against a target.' }}
            />
          </Card>
        </>
      )}

      {/* Create / edit */}
      <Modal
        title={editingBudget ? 'Edit Budget' : 'New Budget'}
        open={modalOpen}
        onCancel={() => { setModalOpen(false); setEditingBudget(null); }}
        onOk={() => form.submit()}
        confirmLoading={saving}
        okText={editingBudget ? 'Save' : 'Create'}
        destroyOnClose
      >
        <Form form={form} layout="vertical" onFinish={handleSubmit} preserve={false}>
          <Form.Item
            name="name"
            label="Name"
            rules={[{ required: true, message: 'Give this budget a name' }]}
          >
            <Input placeholder="e.g. Marketing - Q1" />
          </Form.Item>

          <Form.Item
            name="category"
            label="Category"
            extra="Leave empty to budget across every category"
          >
            <Select allowClear placeholder="All categories">
              {categories.map((category) => (
                <Option key={category.value || category.id} value={category.value || category.id}>
                  {category.label || category.name}
                </Option>
              ))}
            </Select>
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item name="period_type" label="Period" rules={[{ required: true }]}>
                <Select onChange={handlePeriodTypeChange}>
                  <Option value="MONTHLY">Monthly</Option>
                  <Option value="QUARTERLY">Quarterly</Option>
                  <Option value="YEARLY">Yearly</Option>
                  <Option value="CUSTOM">Custom</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item
                name="amount"
                label="Budget Amount"
                rules={[{ required: true, message: 'Enter the budgeted amount' }]}
              >
                <InputNumber
                  min={0.01}
                  style={{ width: '100%' }}
                  prefix="₹"
                  formatter={(value) => `${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ',')}
                  parser={(value) => `${value ?? ''}`.replace(/,/g, '')}
                />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item
            name="period"
            label="Start / End"
            rules={[{ required: true, message: 'Pick the period this budget covers' }]}
          >
            <RangePicker style={{ width: '100%' }} format="YYYY-MM-DD" />
          </Form.Item>

          <Row gutter={12}>
            <Col span={12}>
              <Form.Item
                name="alert_threshold"
                label="Alert at (%)"
                extra="Flagged as 'at risk' from here"
                rules={[{ required: true, message: 'Set an alert threshold' }]}
              >
                <InputNumber min={1} max={100} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col span={12}>
              <Form.Item name="include_payroll" label="Include payroll" valuePropName="checked">
                <Switch />
              </Form.Item>
              <Form.Item name="is_active" label="Active" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Form.Item name="notes" label="Notes">
            <TextArea rows={2} placeholder="Optional" />
          </Form.Item>
        </Form>
      </Modal>
    </div>
  );
};

export default Budgets;
